import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Sentry, initSentry } from "../lib/sentry";
import { initPostHog } from "../lib/posthog";
import { IkomatchExitIntent } from "../components/ikomatch-exit-intent";

initPostHog();

initSentry();

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-extrabold text-ink">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-ink">Page not found</h2>
        <p className="mt-2 text-sm text-muted-ink">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl bg-accent px-5 py-3 text-sm font-bold text-accent-ink transition hover:bg-accent-press"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-ink">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-ink">Something went wrong. Try again or head home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-xl bg-accent px-5 py-3 text-sm font-bold text-accent-ink transition hover:bg-accent-press"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-xl border border-line bg-panel px-5 py-3 text-sm font-medium text-ink transition hover:border-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "DeckScore - Score your pitch deck free, then reach real investors" },
      {
        name: "description",
        content:
          "First-time founder with a finished deck and no network? Score your deck free in 2 minutes, then reach 10+ accelerators & VCs with one form.",
      },
      { property: "og:site_name", content: "DeckScore" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@deckscore" },
      { name: "theme-color", content: "#B79AEF" },
      { name: "google-site-verification", content: "IVaoENcMOkksxhFu-Y-JVZB37tlj8QXzIhZretMGfhI" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico?v=2", sizes: "any" },
      { rel: "icon", href: "/icon.svg?v=2", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=2" },
      { rel: "manifest", href: "/site.webmanifest?v=2" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800&family=Caveat:wght@500;600&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function SentryFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <p className="text-center text-sm text-ink">
        Something went wrong - our team has been notified.
      </p>
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    // Capture ?ref=<code> from any inbound URL (home, /upload, etc.) and
    // persist it in localStorage so it survives the upload → login → analyze
    // path. Cleared post-consumption downstream.
    import("../lib/ref").then((m) => m.captureRefFromUrl()).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    // Affiliate tracking (?aff=CODE): first-party 90-day cookie + click log,
    // then first-touch attribution once a session exists. Fire-and-forget.
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    import("../lib/affiliate")
      .then((m) => {
        if (cancelled) return;
        cleanup = m.initAffiliateTracking();
      })
      .catch(() => { /* non-fatal */ });
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <IkomatchExitIntent />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

